import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFirestore, doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { HoneycombBackground } from '../components/HoneycombBackground';
import { LanguageSelector } from '../components/LanguageSelector';

interface FormData {
  fields: Array<{
    id: string;
    type: string;
    label: string;
    required: boolean;
    options?: string[];
  }>;
}

const PublicQuotationFormV2: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessLogo, setBusinessLogo] = useState<string | null>(null);
  const [clientData, setClientData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!businessId) return;

      try {
        const db = getFirestore();
        const formDoc = await getDoc(doc(db, 'businesses', businessId, 'quotationForm', 'form'));
        
        if (formDoc.exists()) {
          setFormData(formDoc.data() as FormData);
          
          // Obtener el logo del negocio
          const businessDoc = await getDoc(doc(db, 'businesses', businessId));
          if (businessDoc.exists()) {
            const businessData = businessDoc.data();
            if (businessData.logo) {
              setBusinessLogo(businessData.logo);
            }
          }

          // TODO: Obtener datos del cliente
          // Por ahora usamos datos de ejemplo
          setClientData({
            name: "Juan Pérez",
            email: "juan@example.com",
            phone: "+1 234 567 890",
            address: "Calle Principal 123",
            clientSince: "2023-01-15"
          });
        } else {
          setError('Form not found');
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error loading form');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessId || !formData) return;

    try {
      const db = getFirestore();
      // Primero obtenemos la referencia al documento del negocio
      const businessQuotationsRef = doc(db, 'quotations', businessId);
      
      // Luego creamos una nueva cotización en la subcolección submissions
      await addDoc(collection(businessQuotationsRef, 'submissions'), {
        ...formValues,
        status: 'unread',
        createdAt: new Date()
      });

      toast.success(t('common.success'));
      setFormValues({});
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('common.error'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 relative bg-transparent">
        <div className="flex items-center justify-center min-h-48 text-gray-600">
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 relative bg-transparent">
        <div className="flex items-center justify-center min-h-48 text-red-600">
          {t('common.formNotFound')}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 relative bg-transparent">
      <HoneycombBackground />
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-4xl bg-white rounded-3xl p-10 shadow-2xl relative z-10">
        <div className="flex items-center justify-between mb-10 gap-6 border-b border-gray-200 pb-6">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 m-0 leading-tight">
              {t('quotations.formBuilder.title')}
            </h1>
            <p className="text-gray-600 mt-3 mb-0 text-lg leading-relaxed">
              {t('quotations.formBuilder.subtitle')}
            </p>
          </div>
          {businessLogo && (
            <img 
              src={businessLogo} 
              alt="Business Logo" 
              className="h-12 w-auto object-contain rounded-lg p-2 bg-gray-50"
            />
          )}
        </div>

        {clientData && (
          <div className="bg-gray-50 rounded-2xl p-8 mb-10 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-3">
              <div className="w-1 h-6 bg-blue-600 rounded"></div>
              {t('quotations.formBuilder.clientInfo')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex flex-col gap-2 p-4 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:border-blue-500 hover:shadow-md">
                <span className="text-sm text-gray-600 font-medium">
                  {t('quotations.formBuilder.clientName')}
                </span>
                <span className="text-lg text-gray-900 font-semibold">
                  {clientData.name}
                </span>
              </div>
              <div className="flex flex-col gap-2 p-4 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:border-blue-500 hover:shadow-md">
                <span className="text-sm text-gray-600 font-medium">
                  {t('quotations.formBuilder.clientEmail')}
                </span>
                <span className="text-lg text-gray-900 font-semibold">
                  {clientData.email}
                </span>
              </div>
              <div className="flex flex-col gap-2 p-4 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:border-blue-500 hover:shadow-md">
                <span className="text-sm text-gray-600 font-medium">
                  {t('quotations.formBuilder.clientPhone')}
                </span>
                <span className="text-lg text-gray-900 font-semibold">
                  {clientData.phone}
                </span>
              </div>
              <div className="flex flex-col gap-2 p-4 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:border-blue-500 hover:shadow-md">
                <span className="text-sm text-gray-600 font-medium">
                  {t('quotations.formBuilder.clientAddress')}
                </span>
                <span className="text-lg text-gray-900 font-semibold">
                  {clientData.address}
                </span>
              </div>
              <div className="flex flex-col gap-2 p-4 bg-white rounded-xl border border-gray-200 transition-all duration-200 hover:border-blue-500 hover:shadow-md">
                <span className="text-sm text-gray-600 font-medium">
                  {t('quotations.formBuilder.clientSince')}
                </span>
                <span className="text-lg text-gray-900 font-semibold">
                  {new Date(clientData.clientSince).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-8">
          {formData?.fields.map((field) => (
            <div key={field.id} className="flex flex-col gap-3">
              <label htmlFor={field.id} className="text-base text-gray-900 font-semibold flex items-center gap-2">
                {field.label}
                {field.required && <span className="text-red-600">*</span>}
              </label>
              {field.type === 'text' && (
                <input
                  type="text"
                  id={field.id}
                  value={formValues[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  required={field.required}
                  className="p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-200 bg-white focus:outline-none focus:border-blue-500 focus:shadow-lg placeholder-gray-400"
                />
              )}
              {field.type === 'textarea' && (
                <textarea
                  id={field.id}
                  value={formValues[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  required={field.required}
                  className="p-4 border-2 border-gray-200 rounded-xl text-base min-h-32 resize-vertical transition-all duration-200 bg-white focus:outline-none focus:border-blue-500 focus:shadow-lg placeholder-gray-400"
                />
              )}
              {field.type === 'select' && (
                <select
                  id={field.id}
                  value={formValues[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  required={field.required}
                  className="p-4 border-2 border-gray-200 rounded-xl text-base bg-white transition-all duration-200 focus:outline-none focus:border-blue-500 focus:shadow-lg appearance-none bg-no-repeat bg-right-4 bg-center pr-12"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`
                  }}
                >
                  <option value="">{t('common.select')}</option>
                  {field.options?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
          
          <button 
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white p-5 border-none rounded-xl text-lg font-semibold cursor-pointer transition-all duration-200 mt-4 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {t('common.submit')}
          </button>
        </form>
        
        <div className="mt-12 text-center text-gray-600 text-sm pt-8 border-t border-gray-200">
          <span className="block mb-3 font-medium">Powered by</span>
          <img 
            src="https://firebasestorage.googleapis.com/v0/b/brix-2f0c9.appspot.com/o/logo.png?alt=media&token=8c1c7c1c-7c1c-7c1c-7c1c-7c1c7c1c7c1c" 
            alt="Brix Logo" 
            className="h-8 w-auto mx-auto opacity-80 transition-opacity duration-200 hover:opacity-100"
          />
        </div>
      </div>
    </div>
  );
};

export default PublicQuotationFormV2; 